import Indicator from '../Indicator';
import subpixel from '../util/canvasSubpixelOffset';

/** Volume By Price indicator */
export default class VolumeByPriceIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} datasetId - ID to send with the chartAPI request. Most
	 * indicators will not pass this in.
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {boolean} noClip - Whether to clip the indicator
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'volumebyprice',
			datasetId: 'volume', // this will be the id sent to the chartAPI request.  typical indicators will not pass this.
			name: 'Volume by Price',
			description:
				'An indicator that shows the amount of volume for a particular price range.',
			noClip: true,
			inputs: [
				{ name: 'numBars', value: 12 },
				{ name: 'gapSize', value: 1 },
				{ name: 'barWidth', value: 0.5 }
			]
		};
	}

	/** @return {null} */
	getRangeMin() {
		return null;
	}

	/** @return {null} */
	getRangeMax() {
		return null;
	}

	/** @return {object} */
	_getMarkers() {
		return {};
	}

	/** @return {null} */
	_getDataMap() {
		return null;
	}

	/** @param {object} data */
	render(data) {
		if (!data.volume || !data.price) {
			this.core.warn('The Volume indicator is needed to render Volume By Price');
			return;
		}

		const ctx = this.panel.rootContext;
		const gapSize = this.params.inputs[1].value;
		const maxData = this.panel.yAxis.primaryScale.domain();
		const dist = maxData[0] - maxData[1];
		const numBars = this.params.inputs[0].value;
		const size = dist / numBars;
		const barHeight = this.panel.size.height / numBars;
		const bucketsUp = [];
		const bucketsDown = [];
		const bucketsUpWidth = [];
		const xDomain = this.panel.xAxis.primaryScale.domain();
		const paddingLeft = this.panel.size.padding.left;
		let x = 0;
		let y = 0;
		let maxVal = 0;
		let y0 = 0;
		let width = 0;
		const fullWidth = this.panel.size.width * this.params.inputs[2].value;

		// init buckets
		for (y = 0; y < numBars; y++) {
			bucketsUp[y] = 0;
			bucketsDown[y] = 0;
		}

		// populate buckets
		for (x = 0; x < data.volume.length; x++) {
			if (!data.price[x]) {
				continue;
			}

			const diff = x > 0 && data.price[x - 1] ? data.price[x].close - data.price[x - 1].close : 0;
			const val = data.price[x].close;
			const volume = data.volume[x].volume;
			let threshold = maxData[1];

			// don't include datapoints that are offscreen
			if (
				!diff ||
				data.price[x].dateIndex < xDomain[0] ||
				data.price[x].dateIndex > xDomain[1]
			) {
				continue;
			}

			// populate
			for (y = 0; y < numBars; y++) {
				if (val >= threshold && val < threshold + size) {
					if (diff >= 0) {
						bucketsUp[y] += volume;
					} else if (diff < 0) {
						bucketsDown[y] += volume;
					}
				}

				threshold += size;
			}
		}

		// find max row
		for (y = 0; y < numBars; y++) {
			maxVal = Math.max(bucketsUp[y] + bucketsDown[y], maxVal);
		}

		// render bucketsUp
		ctx.beginPath();
		ctx.fillStyle = this.params.style.up.color;

		for (x = bucketsUp.length - 1; x >= 0; x--) {
			width = (bucketsUp[x] / maxVal) * fullWidth;

			subpixel.fillRect(
				ctx,
				paddingLeft + 1, // Plus 1 so as not to draw over the left border
				y0 + gapSize,
				width,
				barHeight - gapSize * 2
			);

			y0 += barHeight;

			bucketsUpWidth[x] = width;
		}

		ctx.fill();
		ctx.stroke();

		// render bucketsDown
		ctx.beginPath();
		ctx.fillStyle = this.params.style.down.color;
		y0 = 0;

		for (x = bucketsDown.length - 1; x >= 0; x--) {
			width = (bucketsDown[x] / maxVal) * fullWidth;

			subpixel.fillRect(
				ctx,
				paddingLeft + bucketsUpWidth[x] + 2, // Plus 2 so as not overlap with up buckets
				y0 + gapSize,
				width,
				barHeight - gapSize * 2
			);

			y0 += barHeight;
		}

		ctx.fill();
	}
}
